import { CHUNK_SIZE } from '../constants.js';
import { BlockId, isLightSource, isTransparent } from './BlockType.js';

/**
 * Block-light propagation engine using BFS flood fill.
 * Computes light levels (0-15) for each voxel in a chunk.
 *
 * Two light channels:
 *   - Sky light: blocks with unobstructed sky above get level 15, attenuates through air
 *   - Block light: emitted by torches, glowstone, lava, etc.
 *
 * The final light value per voxel is max(skyLight, blockLight).
 */

// Index into a CHUNK_SIZE^3 array
function idx(x, y, z) {
  return x + y * CHUNK_SIZE + z * CHUNK_SIZE * CHUNK_SIZE;
}

/**
 * Calculate light for a chunk. Populates chunk.lightMap.
 * @param {Chunk} chunk - The chunk to light
 * @param {World} world - World reference for cross-chunk block lookups
 */
export function calculateChunkLight(chunk, world) {
  const size = CHUNK_SIZE;
  const totalVoxels = size * size * size;

  // Allocate or reuse lightMap
  if (!chunk.lightMap) {
    chunk.lightMap = new Uint8Array(totalVoxels);
  } else {
    chunk.lightMap.fill(0);
  }

  const blocks = chunk.blocks;
  const lightMap = chunk.lightMap;
  const ox = chunk.cx * size;
  const oy = chunk.cy * size;
  const oz = chunk.cz * size;

  // --- Block light via BFS ---
  const blockLight = new Uint8Array(totalVoxels);
  // Use flat array as queue for performance (4 ints per entry: x, y, z, level)
  let queue = new Int32Array(totalVoxels * 4);
  let queueHead = 0;
  let queueTail = 0;

  function enqueueBlock(x, y, z, level) {
    if (queueTail + 4 > queue.length) {
      // Grow queue if needed (rare)
      const newQueue = new Int32Array(queue.length * 2);
      newQueue.set(queue);
      queue = newQueue;
    }
    queue[queueTail++] = x;
    queue[queueTail++] = y;
    queue[queueTail++] = z;
    queue[queueTail++] = level;
  }

  // Seed: find all light sources within this chunk
  for (let y = 0; y < size; y++) {
    for (let z = 0; z < size; z++) {
      for (let x = 0; x < size; x++) {
        const blockId = blocks[idx(x, y, z)];
        const emit = isLightSource(blockId);
        if (emit > 0) {
          blockLight[idx(x, y, z)] = emit;
          enqueueBlock(x, y, z, emit);
        }
      }
    }
  }

  // Seed from neighboring chunks: scan a thin border shell around this chunk.
  // We check blocks within 1 block of the chunk boundary, then also inject
  // light from neighbor chunk lightMaps at the boundary for longer-range sources.
  _seedFromNeighborLightSources(world, chunk, ox, oy, oz, size, blockLight, enqueueBlock);
  _seedFromNeighborLightMaps(world, chunk, ox, oy, oz, size, blockLight, enqueueBlock);

  // BFS propagation within chunk
  while (queueHead < queueTail) {
    const lx = queue[queueHead++];
    const ly = queue[queueHead++];
    const lz = queue[queueHead++];
    const level = queue[queueHead++];

    if (level <= 1) continue;
    const nextLevel = level - 1;

    // 6-directional spread
    _propagateBlock(blocks, blockLight, lx + 1, ly, lz, nextLevel, size, enqueueBlock);
    _propagateBlock(blocks, blockLight, lx - 1, ly, lz, nextLevel, size, enqueueBlock);
    _propagateBlock(blocks, blockLight, lx, ly + 1, lz, nextLevel, size, enqueueBlock);
    _propagateBlock(blocks, blockLight, lx, ly - 1, lz, nextLevel, size, enqueueBlock);
    _propagateBlock(blocks, blockLight, lx, ly, lz + 1, nextLevel, size, enqueueBlock);
    _propagateBlock(blocks, blockLight, lx, ly, lz - 1, nextLevel, size, enqueueBlock);
  }

  // --- Sky light ---
  const skyLight = new Uint8Array(totalVoxels);
  let skyQueue = new Int32Array(totalVoxels * 4);
  let skyHead = 0;
  let skyTail = 0;

  function enqueueSky(x, y, z, level) {
    if (skyTail + 4 > skyQueue.length) {
      const newQ = new Int32Array(skyQueue.length * 2);
      newQ.set(skyQueue);
      skyQueue = newQ;
    }
    skyQueue[skyTail++] = x;
    skyQueue[skyTail++] = y;
    skyQueue[skyTail++] = z;
    skyQueue[skyTail++] = level;
  }

  for (let z = 0; z < size; z++) {
    for (let x = 0; x < size; x++) {
      // Check if column above is clear to the sky
      let skyExposed = true;
      const wx = ox + x;
      const wz = oz + z;
      const chunkTopY = oy + size;

      // Check upward through loaded chunks (cap at reasonable height)
      for (let wy = chunkTopY; wy < chunkTopY + 128; wy++) {
        const b = world.getBlock(wx, wy, wz);
        if (b !== BlockId.AIR && !isTransparent(b)) {
          skyExposed = false;
          break;
        }
      }

      if (skyExposed) {
        // Trace downward within chunk until hitting opaque block
        for (let y = size - 1; y >= 0; y--) {
          const blockId = blocks[idx(x, y, z)];
          if (blockId !== BlockId.AIR && !isTransparent(blockId)) break;
          skyLight[idx(x, y, z)] = 15;
          enqueueSky(x, y, z, 15);
        }
      }
    }
  }

  // BFS for sky light spread (horizontal + vertical)
  while (skyHead < skyTail) {
    const lx = skyQueue[skyHead++];
    const ly = skyQueue[skyHead++];
    const lz = skyQueue[skyHead++];
    const level = skyQueue[skyHead++];

    if (level <= 1) continue;
    const nextLevel = level - 1;

    _propagateBlock(blocks, skyLight, lx + 1, ly, lz, nextLevel, size, enqueueSky);
    _propagateBlock(blocks, skyLight, lx - 1, ly, lz, nextLevel, size, enqueueSky);
    _propagateBlock(blocks, skyLight, lx, ly + 1, lz, nextLevel, size, enqueueSky);
    _propagateBlock(blocks, skyLight, lx, ly - 1, lz, nextLevel, size, enqueueSky);
    _propagateBlock(blocks, skyLight, lx, ly, lz + 1, nextLevel, size, enqueueSky);
    _propagateBlock(blocks, skyLight, lx, ly, lz - 1, nextLevel, size, enqueueSky);
  }

  // Merge: lightMap = max(skyLight, blockLight)
  for (let i = 0; i < totalVoxels; i++) {
    lightMap[i] = Math.max(skyLight[i], blockLight[i]);
  }
}

/**
 * Scan a 1-block shell around the chunk for direct light sources.
 * This catches torches/lava/etc placed just outside the chunk boundary.
 */
function _seedFromNeighborLightSources(world, chunk, ox, oy, oz, size, blockLight, enqueue) {
  // Check 6 faces of the chunk, 1 block outward
  const faces = [
    { axis: 0, dir: -1, fixed: -1 },   // -X face
    { axis: 0, dir: 1, fixed: size },   // +X face
    { axis: 1, dir: -1, fixed: -1 },   // -Y face
    { axis: 1, dir: 1, fixed: size },   // +Y face
    { axis: 2, dir: -1, fixed: -1 },   // -Z face
    { axis: 2, dir: 1, fixed: size },   // +Z face
  ];

  for (const face of faces) {
    for (let a = 0; a < size; a++) {
      for (let b = 0; b < size; b++) {
        let lx, ly, lz;
        if (face.axis === 0) { lx = face.fixed; ly = a; lz = b; }
        else if (face.axis === 1) { lx = a; ly = face.fixed; lz = b; }
        else { lx = a; ly = b; lz = face.fixed; }

        const wx = ox + lx;
        const wy = oy + ly;
        const wz = oz + lz;
        const blockId = world.getBlock(wx, wy, wz);
        const emit = isLightSource(blockId);
        if (emit > 0) {
          // Seed the chunk-interior voxel adjacent to this light source
          let entryX, entryY, entryZ;
          if (face.axis === 0) { entryX = face.dir < 0 ? 0 : size - 1; entryY = a; entryZ = b; }
          else if (face.axis === 1) { entryX = a; entryY = face.dir < 0 ? 0 : size - 1; entryZ = b; }
          else { entryX = a; entryY = b; entryZ = face.dir < 0 ? 0 : size - 1; }

          const entryBlock = chunk.blocks[idx(entryX, entryY, entryZ)];
          if (entryBlock !== BlockId.AIR && !isTransparent(entryBlock)) continue;

          const level = emit - 1; // 1 block distance
          if (level > 0 && blockLight[idx(entryX, entryY, entryZ)] < level) {
            blockLight[idx(entryX, entryY, entryZ)] = level;
            enqueue(entryX, entryY, entryZ, level);
          }
        }
      }
    }
  }
}

/**
 * Read light values from neighbor chunks' already-computed lightMaps
 * and inject them at this chunk's boundary faces.
 * This handles long-range light propagation across chunk boundaries.
 */
function _seedFromNeighborLightMaps(world, chunk, ox, oy, oz, size, blockLight, enqueue) {
  // For each of the 6 faces, read the neighbor chunk's lightMap at the shared boundary
  const neighborOffsets = [
    { dcx: -1, dcy: 0, dcz: 0, srcCoord: (a, b) => [size - 1, a, b], dstCoord: (a, b) => [0, a, b] },
    { dcx: 1, dcy: 0, dcz: 0, srcCoord: (a, b) => [0, a, b], dstCoord: (a, b) => [size - 1, a, b] },
    { dcx: 0, dcy: -1, dcz: 0, srcCoord: (a, b) => [a, size - 1, b], dstCoord: (a, b) => [a, 0, b] },
    { dcx: 0, dcy: 1, dcz: 0, srcCoord: (a, b) => [a, 0, b], dstCoord: (a, b) => [a, size - 1, b] },
    { dcx: 0, dcy: 0, dcz: -1, srcCoord: (a, b) => [a, b, size - 1], dstCoord: (a, b) => [a, b, 0] },
    { dcx: 0, dcy: 0, dcz: 1, srcCoord: (a, b) => [a, b, 0], dstCoord: (a, b) => [a, b, size - 1] },
  ];

  for (const n of neighborOffsets) {
    const neighbor = world.getChunk(chunk.cx + n.dcx, chunk.cy + n.dcy, chunk.cz + n.dcz);
    if (!neighbor || !neighbor.lightMap) continue;

    for (let a = 0; a < size; a++) {
      for (let b = 0; b < size; b++) {
        const [sx, sy, sz] = n.srcCoord(a, b);
        const neighborLight = neighbor.lightMap[idx(sx, sy, sz)];
        if (neighborLight <= 1) continue;

        const [dx, dy, dz] = n.dstCoord(a, b);
        const entryBlock = chunk.blocks[idx(dx, dy, dz)];
        if (entryBlock !== BlockId.AIR && !isTransparent(entryBlock)) continue;

        const level = neighborLight - 1;
        if (blockLight[idx(dx, dy, dz)] < level) {
          blockLight[idx(dx, dy, dz)] = level;
          enqueue(dx, dy, dz, level);
        }
      }
    }
  }
}

/**
 * Propagate light to a neighboring voxel within the chunk.
 */
function _propagateBlock(blocks, lightArr, x, y, z, level, size, enqueue) {
  if (x < 0 || x >= size || y < 0 || y >= size || z < 0 || z >= size) return;

  const i = idx(x, y, z);
  // Opaque blocks block light
  const blockId = blocks[i];
  if (blockId !== BlockId.AIR && !isTransparent(blockId)) return;

  // Only update if we'd increase the light level
  if (lightArr[i] >= level) return;
  lightArr[i] = level;
  enqueue(x, y, z, level);
}

/**
 * Get the light level at a specific position within or near a chunk.
 * Used by ChunkMesher for face lighting.
 * @param {Chunk} chunk - The chunk being meshed
 * @param {World} world - World reference for cross-chunk lookups
 * @param {number} lx - Local x coordinate (can be outside 0..CHUNK_SIZE-1)
 * @param {number} ly - Local y coordinate
 * @param {number} lz - Local z coordinate
 * @returns {number} Light level 0-15
 */
export function getLightAt(chunk, world, lx, ly, lz) {
  if (lx >= 0 && lx < CHUNK_SIZE &&
      ly >= 0 && ly < CHUNK_SIZE &&
      lz >= 0 && lz < CHUNK_SIZE) {
    if (!chunk.lightMap) return 15;  // fallback: full brightness if not computed
    return chunk.lightMap[idx(lx, ly, lz)];
  }

  // Cross-chunk lookup
  const wx = chunk.cx * CHUNK_SIZE + lx;
  const wy = chunk.cy * CHUNK_SIZE + ly;
  const wz = chunk.cz * CHUNK_SIZE + lz;
  const cx = Math.floor(wx / CHUNK_SIZE);
  const cy = Math.floor(wy / CHUNK_SIZE);
  const cz = Math.floor(wz / CHUNK_SIZE);
  const neighborChunk = world.getChunk(cx, cy, cz);
  if (!neighborChunk || !neighborChunk.lightMap) return 15;

  const nlx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
  const nly = ((wy % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
  const nlz = ((wz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
  return neighborChunk.lightMap[idx(nlx, nly, nlz)];
}
